import { getEmployeeAssignments, getResponsibilityAssignment, getRoleAssignment } from "@/api";
import { useReadinessRecalcRefresh } from "./useReadinessRecalcRefresh";

/**
 * Shared by every view that renders PathToReadinessTreeList with editable
 * importance. Expects the host to expose `dataSource`, `pagination`,
 * `employeeStore`, `employeeData`, `cachedEmpId` and `cachedAccountID`.
 */
export default {
  setup() {
    const { pendingIds, failedIds, isPending, hasFailed, start, cancel } =
      useReadinessRecalcRefresh();
    return {
      readinessPendingIds: pendingIds,
      readinessFailedIds: failedIds,
      readinessIsPending: isPending,
      readinessHasFailed: hasFailed,
      startReadinessRecalc: start,
      cancelReadinessRecalc: cancel,
    };
  },
  watch: {
    readinessIsPending() {
      this.emitReadinessRecalcState();
    },
    readinessHasFailed() {
      this.emitReadinessRecalcState();
    },
    "$route.params.id"() {
      this.cancelReadinessRecalc();
    },
    "$route.query.employeeId"() {
      this.cancelReadinessRecalc();
    },
  },
  methods: {
    emitReadinessRecalcState() {
      this.$emit("readiness-recalc-change", {
        pending: this.readinessIsPending,
        failed: this.readinessHasFailed,
      });
    },

    /** Row ids whose readiness an importance change can move: the edited row and its ancestors. */
    getReadinessScopeIds(itemId) {
      const ids = [];
      let row = this.dataSource.find((item) => item.id === itemId);
      while (row) {
        ids.push(row.id);
        const parentId = row.parentId;
        row = this.dataSource.find((item) => item.id === parentId);
      }
      return ids;
    },

    patchRowsReadiness(results, prefix) {
      (results || []).forEach((result) => {
        const item = this.dataSource.find((row) => row.id === `${prefix}_${result.id}`);
        if (!item) return;
        item.readiness = result.current_readiness;
        if (item.data) item.data.readiness = result.current_readiness;
      });
    },

    /** Re-fetch once the recalculation is done and patch values in place; no table rebuild. */
    async refetchReadinessInPlace(ids) {
      const empId = this.cachedEmpId || this.$route?.params?.id || this.$route?.query?.employeeId;
      const accountID =
        this.cachedAccountID || JSON.parse(localStorage.getItem("currentAccountID"));
      const rolesOfPosition = new Set();
      const responsibilitiesOfRole = new Set();
      const directOfPosition = new Set();
      ids.forEach((id) => {
        const row = this.dataSource.find((item) => item.id === id);
        const parent = row && this.dataSource.find((item) => item.id === row.parentId);
        if (!parent) return;
        if (parent.data?.type === "position") {
          (row.data?.type === "role" ? rolesOfPosition : directOfPosition).add(
            String(parent.assignment_id)
          );
        } else if (parent.data?.type === "role") {
          responsibilitiesOfRole.add(String(parent.assignment_id));
          rolesOfPosition.add(String(parent.parentId).replace("position_", ""));
        }
      });

      const patch = (request, prefix) =>
        request.then(({ data }) => this.patchRowsReadiness(data?.results, prefix));
      const requests = [
        patch(
          getEmployeeAssignments(
            accountID,
            empId,
            this.pagination.limit,
            this.pagination.offset,
            "active"
          ),
          "position"
        ),
        ...[...rolesOfPosition].map((positionId) =>
          patch(getRoleAssignment(accountID, empId, positionId, null), "role")
        ),
        ...[...responsibilitiesOfRole].map((roleId) =>
          patch(
            getResponsibilityAssignment(accountID, roleId, "active", "active"),
            "responsibility"
          )
        ),
      ];
      if (typeof this.fetchDirectResponsibilityAssignments === "function") {
        directOfPosition.forEach((positionId) =>
          requests.push(
            patch(
              this.fetchDirectResponsibilityAssignments(accountID, empId, positionId),
              "responsibility"
            )
          )
        );
      }
      requests.push(
        this.employeeStore.fetchCurrentEmployee(empId, true).then((fresh) => {
          if (!fresh) return;
          this.$store.commit("employeeStore/setCurrentEmployee", fresh);
          this.employeeStore.setCurrentEmployee(fresh);
          this.employeeData = { ...fresh };
        })
      );

      await Promise.all(requests);
      this.$parent?.$refs?.employeeSideBar?.recalculateEmployeeReadiness(empId);
    },

    /** One poll, one scoped re-fetch; further changes join the pending scope. */
    startReadinessRecalcRefresh(itemId) {
      const empId = this.cachedEmpId || this.$route?.params?.id || this.$route?.query?.employeeId;
      const accountID =
        this.cachedAccountID || JSON.parse(localStorage.getItem("currentAccountID"));
      return this.startReadinessRecalc({
        accountId: accountID,
        employeeId: empId,
        ids: this.getReadinessScopeIds(itemId),
        refetch: (ids) => this.refetchReadinessInPlace(ids),
        onFailed: (message) => this.$notifications.warning(message),
      });
    },
  },
};
